import { NextFunction, Request, Response } from "express";
import mongoose from "mongoose";

import { School, Teacher } from "../models";

// Extended Request interface for authenticated users
interface AuthRequest extends Request {
  user?: {
    userId: string;
    role: string;
    schoolId?: string;
  };
  school?: {
    id: string;
    name: string;
    isVerified: boolean;
  };
}

/**
 * Middleware to attach school context to request
 * This middleware should be used after authentication and role verification
 * Supports both school and teacher roles
 */
export const attachSchoolContext = async (
  req: Request,
  res: Response,
  next: NextFunction,
): Promise<void> => {
  try {
    const authReq = req as AuthRequest;
    const userRole = authReq.user?.role;
    const userId = authReq.user?.userId;

    if (userRole === "school") {
      if (!userId) {
        res.status(400).json({
          success: false,
          message: "User ID is required",
          error: "No user ID found in authentication context",
        });
        return;
      }

      const userIdObjectId =
        mongoose.Types.ObjectId.createFromHexString(userId);
      const school = await School.findOne({
        userId: userIdObjectId,
      }).select("schoolName isVerified status");

      if (!school) {
        res.status(404).json({
          success: false,
          message: "School profile not found",
          error: "No school profile exists for this user",
        });
        return;
      }

      authReq.school = {
        id: school._id.toString(),
        name: school.schoolName,
        isVerified: school.isVerified,
      };

      if (authReq.user) {
        authReq.user.schoolId = authReq.school.id;
      }
    } else if (userRole === "teacher") {
      if (!userId) {
        res.status(400).json({
          success: false,
          message: "User ID is required",
          error: "No user ID found in authentication context",
        });
        return;
      }

      const userIdObjectId =
        mongoose.Types.ObjectId.createFromHexString(userId);
      const teacher = await Teacher.findOne({
        userId: userIdObjectId,
        isApproved: true,
      }).select("schoolId");

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found or not approved",
          error: "No approved teacher profile exists for this user",
        });
        return;
      }

      const school = await School.findById(teacher.schoolId).select(
        "schoolName isVerified status",
      );

      if (school) {
        authReq.school = {
          id: school._id.toString(),
          name: school.schoolName,
          isVerified: school.isVerified,
        };
      }

      if (authReq.user) {
        authReq.user.schoolId = teacher.schoolId.toString();
      }
    }

    next();
  } catch {
    res.status(500).json({
      success: false,
      message: "Internal server error",
      error: "Failed to attach school context",
    });
  }
};

/**
 * Middleware to require school context
 * This middleware should be used after attachSchoolContext
 */
export const requireSchoolContext = (
  req: Request,
  res: Response,
  next: NextFunction,
): void => {
  const authReq = req as AuthRequest;
  const userRole = authReq.user?.role;

  if (userRole === "school" || userRole === "teacher") {
    if (!authReq.user?.schoolId) {
      res.status(400).json({
        success: false,
        message: "School context is required",
        error: "No school context found",
      });
      return;
    }

    if (authReq.school && !authReq.school.isVerified) {
      res.status(403).json({
        success: false,
        message: "School verification required",
        error: "School must be verified to access this resource",
      });
      return;
    }
  }

  next();
};

/**
 * Middleware to validate school ID in request parameters
 */
export const validateSchoolId = (
  req: Request,
  res: Response,
  next: NextFunction,
): void => {
  const authReq = req as AuthRequest;
  const schoolId = authReq.user?.schoolId;
  const requestedSchoolId = req.params.schoolId || req.query.schoolId;

  if (requestedSchoolId && schoolId !== requestedSchoolId) {
    res.status(403).json({
      success: false,
      message: "Access denied",
      error: "Cannot access resources from other schools",
    });
    return;
  }

  next();
};
